import React, { ReactNode } from 'react';
import { AssetUser, IAsset } from '../IAsset';
import { AttributeValue } from '../IAssetRow';
import parse from 'html-react-parser';
import { dateUtils } from 'utils/date-utils';
import { Link } from 'app/components/ExternalLink';
import styled from 'styled-components';
import { Button } from 'app/components/BasicButtons/Button';
import { translations } from 'locales/translations';
import { useTranslation } from 'react-i18next';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { List, ListItem, ListItemText, useTheme } from '@material-ui/core';
import { bookitColors } from 'styles/colors/bookitColors';
import BasicTypography from 'app/components/Typography/BasicTypography';
import clsx from 'clsx';
import { DemSize } from 'app/components/BasicCards';

export const DateRenderer = (
  props: AttributeValue<IAsset>,
): React.ReactNode => {
  let date = props.value as Date;
  return <>{dateUtils.shortDateFormat(date)}</>;
};
const ShortText = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  * {
    margin: 0px;
  }
`;
const FullText = styled('span')`
  * {
    margin: 0px;
  }
`;
export const ImageGallery = styled('div')(({ theme }) => ({
  '&.imageSlider': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gap: 8,
    height: 'auto',
    alignSelf: 'stretch',
    '& .imgSliderCenter': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 10,
      alignSelf: 'stretch',
      '& .cardImage': {
        display: 'flex',
        flexDirection: 'column',
        height: '340px',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        '&.cardImageMobile': {
          height: '220px',
        },
        '& .frame': {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          alignSelf: 'stretch',
          flex: '1 0 0',
          borderRadius: '4px',
          '& .mediaStyle': {
            imageRendering: '-webkit-optimize-contrast',
            backgroundSize: 'auto',
            width: '100%',
            backgroundBlendMode: 'multiply',
            minWidth: 'unset',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 15,
            // '&.position_vertical': {
            //   height: '100%',
            //   width: '100%',
            // },
          },
        },
      },
    },
  },
}));
export const HtmlRenderer = (props: AttributeValue<IAsset>): ReactNode => {
  const { t } = useTranslation();
  const [fullText, setFullText] = React.useState<boolean>(false);
  const [needButton, setNeedButton] = React.useState<boolean>(false);
  let html = props.value as string;
  const text = parse(html);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (ref.current !== null) {
      if (
        ref.current.clientHeight === ref.current.scrollHeight &&
        ref.current.clientWidth === ref.current.scrollWidth
      ) {
        setNeedButton(false);
      } else {
        setNeedButton(true);
      }
    }
  }, []);
  return (
    <>
      <div>
        {fullText ? (
          <FullText>{text}</FullText>
        ) : (
          <ShortText ref={ref}>{text}</ShortText>
        )}
        {needButton && (
          <div>
            <Button
              variant="text"
              size="small"
              onClick={() => setFullText(!fullText)}
              endIcon={<Icon icon={fullText ? 'chevron-up' : 'chevron-down'} />}
            >
              {fullText ? t(translations.ShowLess) : t(translations.ShowMore)}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export function ArrayRenderer<T>(
  callback: (value: T, original: IAsset | undefined) => ReactNode | undefined,
) {
  const mapper = (props: AttributeValue<IAsset>) => {
    if (!!props.value && !!props.value.length) {
      return (
        // <ul style={{ margin: 0 }}>
        //   {props.value.map(v => (
        //     <li>{callback(v, props.original)}</li>
        //   ))}
        // </ul>
        <List dense disablePadding key={`array_list_${props.original.Id}`}>
          {props.value.map((v, indx) => (
            <ListItem
              dense
              disableGutters
              key={`array_listItem_${props.original.Id}_${indx}`}
            >
              <ListItemText
                style={{ margin: 0 }}
                key={`array_listItemText_${props.original.Id}_${indx}`}
              >
                {callback(v, props.original)}
              </ListItemText>
            </ListItem>
          ))}
        </List>
      );
    }
  };
  return mapper;
}
export const AssetDetailsImageRender = (
  props: AttributeValue<IAsset>,
): ReactNode => {
  const theme = useTheme();
  const defaultImgSrc = process.env.PUBLIC_URL + `/images/400x300.png`;
  const defaultImgMobileSrc = process.env.PUBLIC_URL + `/images/200x150.png`;
  const defaultImg = React.useMemo(() => {
    return props.shortView ? defaultImgMobileSrc : defaultImgSrc;
  }, [defaultImgMobileSrc, defaultImgSrc, props.shortView]);

  const [pathSrc, setPathSrc] = React.useState<string | undefined>(defaultImg);

  const [loadingImg, setLoadingImg] = React.useState<boolean | undefined>(
    undefined,
  );
  const [imageDimensions, setImageDimensions] = React.useState<
    DemSize | undefined
  >(undefined);
  const checkImage = React.useCallback(
    async (path: any) =>
      new Promise<{
        path?: string;
        status: string;
        height: number;
        width: number;
      }>(resolve => {
        const img = new Image();
        img.src = props.value;
        img.onload = () =>
          resolve({ path, status: 'ok', height: img.height, width: img.width });
        img.onerror = () =>
          resolve({
            path,
            status: 'error',
            height: props.shortView ? 150 : 300,
            width: props.shortView ? 200 : 400,
          });
      }),
    [props.shortView, props.value],
  );
  React.useEffect(() => {
    let active = props.value !== undefined && loadingImg === undefined;
    if (active) {
      if (loadingImg) return;
      setLoadingImg(true);
      (async () => {
        try {
          const res = await checkImage(props.value);
          if (res.status === 'ok') {
            setPathSrc(res.path);
            setImageDimensions({
              height: res.height,
              width: res.width,
            });
            setLoadingImg(false);
          } else {
            setPathSrc(defaultImg);
            setImageDimensions({
              height: res.height,
              width: res.width,
            });
            setLoadingImg(false);
          }
        } catch {
          setPathSrc(defaultImg);
          setImageDimensions({
            height: props.shortView ? 150 : 300,
            width: props.shortView ? 200 : 400,
          });
          setLoadingImg(false);
        }
      })();
    }
    return () => {
      active = false;
    };
  }, [checkImage, defaultImg, loadingImg, props.shortView, props.value]);
  const backgroundSize = React.useMemo(() => {
    if (!!imageDimensions) {
      let horizontal = imageDimensions.width - imageDimensions.height > 0;
      if (
        imageDimensions.height === imageDimensions.width ||
        (horizontal && imageDimensions.width / 1.1 < imageDimensions.height) ||
        (!horizontal && imageDimensions.height / 1.1 < imageDimensions.width)
      ) {
        return 'contain';
      }
      if (!horizontal) {
        if (imageDimensions.height < (props.shortView ? 220 : 340) / 1.8) {
          return 'contain';
        } else {
          return imageDimensions.height / imageDimensions.width > 1.8
            ? 'cover'
            : 'contain';
        }
      } else {
        if (imageDimensions.width < (props.shortView ? 220 : 340) / 1.8) {
          return 'contain';
        } else {
          return imageDimensions.width / imageDimensions.height < 1.8
            ? 'cover'
            : 'contain';
        }
      }
    }
    return undefined;
  }, [imageDimensions, props.shortView]);
  return !!pathSrc && loadingImg === false ? (
    <ImageGallery className={'imageSlider'} theme={theme}>
      <div className={'imgSliderCenter'}>
        <div
          className={clsx('cardImage', { cardImageMobile: props.shortView })}
        >
          <div className={'frame'}>
            <div
              className={'mediaStyle'}
              style={{
                backgroundImage: `url(${pathSrc})`,
                backgroundSize: backgroundSize,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                paddingTop: props.shortView ? 220 : 340,
              }}
            ></div>
            {/* <img
              src={props.value}
              alt=""
              // style={
              //   (setting.GetNullableByKey(AllowedSettings.AssetImageSizeType) ??
              //     '2') === '1' && !isMobile
              //     ? { maxWidth: '400px', maxHeight: '300px' }
              //     : { maxWidth: '200px', maxHeight: '150px' }
              // }
            /> */}
          </div>
        </div>
      </div>
    </ImageGallery>
  ) : (
    <></>
  );
};
export function UsersRenderer() {
  return ArrayRenderer<AssetUser>(v => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} key={v.Name}>
      <BasicTypography component={'span'} variant={'bodyS'}>
        {v.Name}
      </BasicTypography>
      {!!v.Email && (
        <Link
          style={{ color: bookitColors.primary.regular }}
          href={'mailto:' + v.Email}
          useInlineUrl={true}
        >
          {v.Email}
        </Link>
      )}

      {!!v.OfficeTel && (
        <BasicTypography component={'span'} variant={'bodyS'}>
          {v.OfficeTel}
        </BasicTypography>
      )}
    </div>
  ));
}
